// Add-to-calendar links for the booked call. Google and Outlook take the
// event in the URL; Apple takes the server-built .ics file.

import { BOOKING_CONFIRMATION_COPY } from "./booking-confirmation-copy";

export type CalendarKind = "google" | "apple" | "outlook";

const CALL_MINUTES = 15;

interface CalendarEvent {
  title: string;
  details: string;
  startIso: string;
}

function callWindow(startIso: string): { start: Date; end: Date } {
  const start = new Date(startIso);
  return { start, end: new Date(start.getTime() + CALL_MINUTES * 60_000) };
}

/** `20261003T163000Z` */
function compactUtc(at: Date): string {
  return at
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** `2026-10-03T16:30:00Z` */
function isoUtc(at: Date): string {
  return at.toISOString().replace(/\.\d{3}/, "");
}

function query(params: [string, string][]): string {
  return params
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
}

export function googleCalendarUrl(event: CalendarEvent): string {
  const { start, end } = callWindow(event.startIso);
  return `https://calendar.google.com/calendar/render?${query([
    ["action", "TEMPLATE"],
    ["text", event.title],
    ["dates", `${compactUtc(start)}/${compactUtc(end)}`],
    ["details", event.details],
  ])}`;
}

export function outlookCalendarUrl(event: CalendarEvent): string {
  const { start, end } = callWindow(event.startIso);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${query([
    ["path", "/calendar/action/compose"],
    ["rru", "addevent"],
    ["subject", event.title],
    ["body", event.details],
    ["startdt", isoUtc(start)],
    ["enddt", isoUtc(end)],
  ])}`;
}

/** The server-built calendar file for the quote's upcoming call; the
 * quote session cookie authenticates the navigation. */
export function icsUrl(apiEndpoint: string, quoteId: string): string {
  return `${apiEndpoint}/api/v1/quotes/${quoteId}/call.ics`;
}

const IN_APP_BROWSER = /FBAN|FBAV|FB_IAB|Instagram/;

/** Facebook and Instagram's in-app browsers cannot hand a .ics to the
 * phone's calendar or open another app's deeplink. */
export function isInAppBrowser(userAgent: string): boolean {
  return IN_APP_BROWSER.test(userAgent);
}

/** The calendars offered, in order, for this browser. */
export function calendarKindsFor(userAgent: string): CalendarKind[] {
  return isInAppBrowser(userAgent)
    ? ["google"]
    : ["google", "apple", "outlook"];
}

export function calendarLabel(kind: CalendarKind): string {
  return BOOKING_CONFIRMATION_COPY.calendarOptions[kind];
}
